import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { findAll, create, update, deleteById } from "@/apis/template-category";
import {
  TemplateCategoryMasterDto,
  TemplateCategoryMasterCreateRequestDto,
  TemplateCategoryMasterUpdateRequestDto,
} from "@/dto/TemplateCategoryDto";
import { useModal } from "mui-modal-provider";
import { Confirm } from "@/organism/Confirm";
import { enqueueSnackbar } from "notistack";

export default function useTemplateCategory() {
  const { id: templateId } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [categories, setCategories] = useState<TemplateCategoryMasterDto[]>([]);
  const [selectedCategory, setSelectedCategory] =
    useState<TemplateCategoryMasterDto | null>(null);
  const { showModal } = useModal();

  /**
   * 템플릿 카테고리 목록 조회
   */
  const fetchCategories = useCallback(async () => {
    if (!templateId) return;

    console.log("카테고리 목록 조회 시작:", new Date().toISOString());
    try {
      setIsLoading(true);
      const templateIdNum = parseInt(templateId);
      const response = await findAll(templateIdNum);
      console.log("카테고리 API 응답 데이터:", response);

      if (response) {
        // 템플릿 ID로 필터링 된 카테고리 목록을 받아옴
        // 응답이 null이거나 undefined인 경우 빈 배열로 처리
        // API 응답이 { list: [], count: 0, page: 1, limit: 10 } 형태로 오는 경우 처리
        let newCategories = [];
        if ("list" in response) {
          // PaginatedCategoryResponse 형태
          newCategories = response.list || [];
        } else {
          // TemplateCategoryMasterDto[] 형태
          newCategories = response || [];
        }

        console.log("새로운 카테고리 목록:", newCategories);
        setCategories(newCategories);
      } else {
        enqueueSnackbar("카테고리 목록을 불러오는데 실패했습니다.", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("카테고리 목록 조회 중 오류 발생:", error);
      enqueueSnackbar("카테고리 목록을 불러오는데 실패했습니다.", {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
      console.log("카테고리 목록 조회 완료:", new Date().toISOString());
    }
  }, [templateId, enqueueSnackbar]);

  /**
   * 강제 새로고침 함수 - 카테고리 상태를 리셋하고 다시 불러옵니다
   */
  const forceRefresh = useCallback(async () => {
    // 상태를 초기화하여 컴포넌트가 리렌더링되도록 합니다
    setCategories([]);

    // 약간의 딜레이 후 데이터를 다시 불러옵니다
    setTimeout(async () => {
      await fetchCategories();
    }, 100);
  }, [fetchCategories]);

  // 컴포넌트 마운트 시 카테고리 목록 조회
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * 카테고리 생성
   */
  const handleCategoryCreate = async (
    data: TemplateCategoryMasterCreateRequestDto
  ) => {
    if (!templateId) {
      enqueueSnackbar("템플릿 ID가 유효하지 않습니다.", { variant: "error" });
      return;
    }

    try {
      setIsLoading(true);

      // templateId 설정
      data.templateId = parseInt(templateId);

      const response = await create(data);
      if (response.success) {
        enqueueSnackbar("카테고리가 성공적으로 등록되었습니다.", {
          variant: "success",
        });

        // 즉시 새로고침
        await fetchCategories(); // 목록 새로고침을 기다림
        return true;
      } else {
        enqueueSnackbar(`카테고리 등록 실패: ${response.message}`, {
          variant: "error",
        });
        return false;
      }
    } catch (error) {
      console.error("카테고리 등록 중 오류 발생:", error);
      enqueueSnackbar("카테고리 등록에 실패했습니다.", { variant: "error" });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 카테고리 수정
   */
  const handleCategoryUpdate = async (
    data: TemplateCategoryMasterUpdateRequestDto
  ) => {
    try {
      setIsLoading(true);

      const response = await update(data);
      if (response.success) {
        enqueueSnackbar("카테고리가 성공적으로 수정되었습니다.", {
          variant: "success",
        });

        // 즉시 새로고침
        await fetchCategories(); // 목록 새로고침을 기다림
        return true;
      } else {
        enqueueSnackbar(`카테고리 수정 실패: ${response.message}`, {
          variant: "error",
        });
        return false;
      }
    } catch (error) {
      console.error("카테고리 수정 중 오류 발생:", error);
      enqueueSnackbar("카테고리 수정에 실패했습니다.", { variant: "error" });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 카테고리 삭제
   */
  const handleCategoryDelete = (
    categoryId: number,
    categoryName: string,
    isPage: boolean
  ) => {
    // 페이지인 경우 하위 섹션이 있는지 확인
    if (isPage) {
      const sections = getSectionsByPageId(categoryId);
      if (sections.length > 0) {
        enqueueSnackbar(
          "하위 카테고리가 있는 페이지는 삭제할 수 없습니다. 먼저 하위 카테고리를 삭제해주세요.",
          { variant: "error" }
        );
        return;
      }
    }

    const modal = showModal(Confirm, {
      title: `${isPage ? "페이지" : "섹션"} 삭제`,
      content: `"${categoryName}" ${
        isPage ? "페이지" : "섹션"
      }을 삭제하시겠습니까? ${
        isPage ? "이 페이지에 속한 모든 섹션도 함께 삭제됩니다." : ""
      }`,
      okTitle: "삭제",
      cancelTitle: "취소",
      onOk: async () => {
        modal.destroy();
        try {
          setIsLoading(true);
          // 숫자가 아닌 ID 문자열 변환
          const result = await deleteById(String(categoryId));

          if (result.success) {
            enqueueSnackbar(
              `${isPage ? "페이지" : "섹션"}가 성공적으로 삭제되었습니다.`,
              {
                variant: "success",
              }
            );

            // 즉시 새로고침을 위해 await 추가
            await fetchCategories();
          } else {
            enqueueSnackbar(
              result.message ||
                `${isPage ? "페이지" : "섹션"} 삭제 중 오류가 발생했습니다.`,
              { variant: "error" }
            );
          }
        } catch (error) {
          console.error(
            `${isPage ? "페이지" : "섹션"} 삭제 중 오류 발생:`,
            error
          );
          // 에러 메시지에서 서버 에러 메시지 추출 시도
          let errorMessage = `${
            isPage ? "페이지" : "섹션"
          } 삭제에 실패했습니다.`;
          if (error && typeof error === "object" && "message" in error) {
            errorMessage = error.message as string;
          } else if (error && typeof error === "string") {
            errorMessage = error;
          }

          enqueueSnackbar(errorMessage, {
            variant: "error",
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel: () => {
        modal.destroy();
      },
    });
  };

  /**
   * 페이지 레벨 카테고리만 필터링
   */
  const getPageCategories = useCallback(() => {
    if (!categories || categories.length === 0) return [];

    return categories.filter(
      (category) => Number(category.categoryLevel) === 1
    );
  }, [categories]);

  /**
   * 특정 페이지의 섹션 카테고리 필터링
   */
  const getSectionsByPageId = useCallback(
    (pageId: number) => {
      if (!categories || categories.length === 0) return [];

      return categories.filter(
        (category) =>
          Number(category.categoryLevel) === 2 &&
          Number(category.parentMasterCategoryId) === pageId
      );
    },
    [categories]
  );

  return {
    isLoading,
    categories,
    selectedCategory,
    setSelectedCategory,
    pageCategories: getPageCategories(),
    getSectionsByPageId,
    handleCategoryCreate,
    handleCategoryUpdate,
    handleCategoryDelete,
    fetchCategories,
    forceRefresh,
  };
}
